import { Clock, MapPin, Phone, ArrowUpRight } from "lucide-react";
import { BRAND } from "@/data/content";
import { isOpenNow } from "@/lib/ui";
import { MaskLine, FadeUp } from "@/components/Reveal";

export default function Contact() {
  const openNow = isOpenNow();
  return (
    <section id="contact" data-testid="contact-section" className="relative py-20 md:py-32 overflow-hidden">
      <div className="absolute -bottom-40 right-[-10%] w-[40rem] h-[40rem] rounded-full bg-gold/[0.08] blur-[140px] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
        <div>
          <FadeUp>
            <p className="text-[11px] uppercase tracking-[0.3em] text-gold">Contact &amp; Location</p>
          </FadeUp>
          <h2 className="mt-4 font-display font-semibold text-4xl sm:text-5xl leading-[1.08] tracking-tight text-cream">
            <MaskLine>Order Ahead or</MaskLine>
            <MaskLine delay={0.1}>
              <em className="italic font-light text-gold">Visit Us Today!</em>
            </MaskLine>
          </h2>
          <FadeUp delay={0.2}>
            <p className="mt-6 max-w-md text-cream/65 leading-relaxed">
              Big batch for a celebration, or just a hot samosa before school run? Call ahead and it
              will be ready when you reach the counter.
            </p>
          </FadeUp>
          <FadeUp delay={0.3} className="mt-9 flex flex-col sm:flex-row gap-4">
            <a
              data-testid="contact-call-primary"
              href={`tel:${BRAND.phones[0]}`}
              className="inline-flex items-center justify-center gap-3 rounded-full bg-gold px-8 py-4 text-sm font-semibold tracking-wide text-ink hover:bg-gold-light transition-colors duration-200"
            >
              <Phone className="w-4 h-4" /> Call {BRAND.phones[0]}
            </a>
            <a
              data-testid="contact-call-secondary"
              href={`tel:${BRAND.phones[1]}`}
              className="inline-flex items-center justify-center gap-3 rounded-full border border-gold/40 px-8 py-4 text-sm font-semibold tracking-wide text-gold hover:bg-gold/10 transition-colors duration-200"
            >
              <Phone className="w-4 h-4" /> Call {BRAND.phones[1]}
            </a>
          </FadeUp>
          <FadeUp delay={0.4}>
            <p className="mt-6 text-sm text-cream/50">
              Ask for <span className="text-cream font-semibold">{BRAND.owner}</span> at the counter.
            </p>
          </FadeUp>
        </div>

        <div className="space-y-5">
          <FadeUp delay={0.1}>
            <div data-testid="contact-hours-card" className="rounded-3xl bg-cocoa-900 border border-gold/15 p-7 flex items-start gap-5">
              <div className="w-12 h-12 shrink-0 rounded-full border border-gold/30 flex items-center justify-center text-gold">
                <Clock className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-display font-semibold text-xl text-cream">Operating Hours</h3>
                  <span
                    data-testid="contact-open-chip"
                    className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${
                      openNow ? "bg-emerald-500/15 text-emerald-300" : "bg-saffron/15 text-saffron"
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${openNow ? "bg-emerald-400" : "bg-saffron"} animate-pulse`} />
                    {openNow ? "Open now" : "Opens 5 AM"}
                  </span>
                </div>
                <p className="mt-2 text-cream/70">{BRAND.hours} <span className="text-cream/45">· Everyday</span></p>
              </div>
            </div>
          </FadeUp>

          <FadeUp delay={0.2}>
            <div data-testid="contact-address-card" className="rounded-3xl bg-cocoa-900 border border-gold/15 p-7 flex items-start gap-5">
              <div className="w-12 h-12 shrink-0 rounded-full border border-gold/30 flex items-center justify-center text-gold">
                <MapPin className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h3 className="font-display font-semibold text-xl text-cream">Find Us</h3>
                <p className="mt-2 text-cream/70 leading-relaxed">{BRAND.address}</p>
                <a
                  data-testid="contact-directions-link"
                  href={BRAND.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-gold hover:text-gold-light transition-colors duration-200"
                >
                  Get Directions <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </FadeUp>

          <FadeUp delay={0.3}>
            <div className="rounded-3xl border border-gold/15 p-7 bg-gradient-to-b from-cocoa-900 to-cocoa-950">
              <p className="font-bengali text-gold text-2xl leading-snug">আজই আহক — মিষ্টি ল’মখ</p>
              <p className="mt-2 text-sm text-cream/55 leading-relaxed">
                Come today — the mithai is waiting. Pure desi ghee sweets, hot snacks and fresh juice,
                every single day.
              </p>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
